// Owns the restaurant list. Firebase is the source of truth; localStorage is only a cache for an instant first paint.
// Writes are per restaurant (`restaurants/<id>`), optimistic, and never awaited by the UI: a failed write shows a toast.
import { useState, useEffect, useRef, useCallback } from 'react';
import { subscribe, subscribeConnected, patchRestaurant, putRestaurant, putRestaurants, deleteRestaurantForever, toArray, onAuthChange } from '../db.js';

const CACHE_KEY = 'restaurants';

function readCache() {
    try {
        const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function useRestaurants({ enabled, onWriteError }) {
    const [restaurants, setRestaurantsState] = useState(readCache);
    const latest = useRef(restaurants);
    const [syncState, setSyncState] = useState('cache'); // cache | live | offline | error
    const [pending, setPending] = useState(0);
    const [connected, setConnected] = useState(true);
    const cacheTimer = useRef(null);
    const errorRef = useRef(onWriteError);
    errorRef.current = onWriteError;

    const setRestaurants = useCallback((next) => {
        const value = typeof next === 'function' ? next(latest.current) : next;
        latest.current = value;
        setRestaurantsState(value);
        clearTimeout(cacheTimer.current);
        cacheTimer.current = setTimeout(() => {
            try { localStorage.setItem(CACHE_KEY, JSON.stringify(value)); } catch { /* quota: base64 photos can exceed it */ }
        }, 1000);
    }, []);

    // Live listener on the whole list (RTDB sends deltas; Firebase wins, including an empty list)
    useEffect(() => {
        if (!enabled) return undefined;
        const off = subscribe('restaurants', (data) => {
            setRestaurants(toArray(data));
            setSyncState('live');
        }, () => setSyncState('error'));
        let offConnected = () => {};
        try { offConnected = subscribeConnected(setConnected); } catch { /* SDK not loaded */ }
        const offAuth = onAuthChange((ok) => { if (!ok) setSyncState('error'); });
        return () => { off(); offConnected(); offAuth(); };
    }, [enabled, setRestaurants]);

    const track = useCallback((promise) => {
        setPending((n) => n + 1);
        promise
            .catch((err) => { if (errorRef.current) errorRef.current(err); })
            .finally(() => setPending((n) => Math.max(0, n - 1)));
    }, []);

    /** Apply `patch` (object, or fn(current) -> object) to one restaurant: local state now, Firebase in the background. */
    const mutate = useCallback((id, patch) => {
        const current = latest.current.find((r) => r.id === id);
        if (!current) return;
        const p = typeof patch === 'function' ? patch(current) : patch;
        if (!p || Object.keys(p).length === 0) return;
        setRestaurants((prev) => prev.map((r) => (r.id === id ? { ...r, ...p } : r)));
        track(patchRestaurant(id, p));
    }, [setRestaurants, track]);

    const create = useCallback((record) => {
        setRestaurants((prev) => [...prev, record]);
        track(putRestaurant(record));
    }, [setRestaurants, track]);

    const createMany = useCallback((records) => {
        if (!records.length) return;
        setRestaurants((prev) => [...prev, ...records]);
        track(putRestaurants(records));
    }, [setRestaurants, track]);

    const destroy = useCallback((id) => {
        setRestaurants((prev) => prev.filter((r) => r.id !== id));
        track(deleteRestaurantForever(id));
    }, [setRestaurants, track]);

    const getRestaurant = useCallback((id) => latest.current.find((r) => r.id === id) || null, []);

    const sync = !connected ? 'offline' : pending > 0 ? 'syncing' : syncState;

    return { restaurants, setRestaurants, mutate, create, createMany, destroy, getRestaurant, sync, isSyncing: syncState === 'cache' };
}
